import { allowedEmailDomains, MAX_INCOME, MAX_LOAN } from '@/constants/form';
import { CreditRating, USStates } from '@/types/form';
import {
  validateAndFormatUSPhoneNumber,
  validateDOB,
  validateSSN,
} from './validation';

export const getFundedLoansByIncome = (annualIncome: number): number => {
  if (annualIncome <= 35999) return 69;
  if (annualIncome <= 71999) return 539;
  if (annualIncome <= 107999) return 502;
  if (annualIncome <= 143999) return 291;
  return 488;
};

export const stateAbbreviations = Object.values(USStates);

const BLOCKED_NAME_TOKENS = new Set([
  'fuck',
  'fucker',
  'fucking',
  'fuk',
  'fck',
  'fuq',
  'phuck',
  'motherfucker',
  'shit',
  'shithead',
  'bullshit',
  'sht',
  'bitch',
  'biatch',
  'cunt',
  'asshole',
  'arsehole',
  'ahole',
  'dickhead',
  'douche',
  'douchebag',
  'twat',
  'wanker',
  'whore',
  'slut',
  'pussy',
  'jackass',
  'dumbass',
  'dipshit',
  'pissoff',
  'fuckyou',
  'eatshit',
  'nigger',
  'nigga',
  'faggot',
  'kike',
  'spic',
  'wetback',
  'chink',
  'tranny',
  'retard',
  'test',
  'tester',
  'testing',
  'asdf',
  'asdfasdf',
  'qwerty',
  'abc',
  'abcd',
  'aaa',
  'xxx',
  'none',
  'na',
  'unknown',
  'noname',
  'nobody',
  'fake',
  'fakename',
  'firstname',
  'lastname',
  'fname',
  'lname',
  'admin',
  'user',
]);

const LEET_MAP: Record<string, string> = {
  '0': 'o',
  '1': 'i',
  '3': 'e',
  '4': 'a',
  '5': 's',
  '7': 't',
  '8': 'b',
  '@': 'a',
  $: 's',
  '!': 'i',
  '*': '',
};

const normalizeNameToken = (token: string): string =>
  token
    .toLowerCase()
    .replace(/[0134578@$!*]/g, (c) => LEET_MAP[c] ?? c)
    .replace(/[^a-z]/g, '')
    .replace(/(.)\1{2,}/g, '$1$1');

export const isBlockedName = (name: string | null | undefined): boolean => {
  if (!name) return false;
  const tokens = name.split(/[\s\-'.]+/).filter(Boolean);
  return tokens.some((token) => {
    const normalized = normalizeNameToken(token);
    const collapsed = normalized.replace(/(.)\1+/g, '$1');
    return (
      BLOCKED_NAME_TOKENS.has(normalized) || BLOCKED_NAME_TOKENS.has(collapsed)
    );
  });
};

export const borrowSumFactCardValues = (
  borrowSum: string | number | null | undefined
): { amount: number | null; range: string | null } => {
  if (!borrowSum) return { amount: null, range: null };
  const sum = Number(borrowSum.toString().replace(/\D/g, ''));
  if (sum <= 9999) return { amount: 358, range: '$0 - $9,999' };
  if (sum <= 19999) return { amount: 243, range: '$10,000 - $19,999' };
  if (sum <= 29999) return { amount: 191, range: '$20,000 - $29,999' };
  if (sum <= 39999) return { amount: 166, range: '$30,000 - $39,999' };
  if (sum <= 49999) return { amount: 89, range: '$40,000 - $49,999' };
  if (sum <= 59999) return { amount: 82, range: '$50,000 - $59,999' };
  if (sum <= 69999) return { amount: 17, range: '$60,000 - $69,999' };
  if (sum <= 79999) return { amount: 17, range: '$70,000 - $79,999' };
  if (sum <= 250000) return { amount: 80, range: '$80,000 - $250,000' };
  return { amount: null, range: null };
};

export const creditRatingFactCard = (creditRating: CreditRating | null) => {
  switch (creditRating) {
    case 'Excellent (720+)':
      return { amount: 503, label: 'Excellent' };
    case 'Good (660-719)':
      return { amount: 778, label: 'Good' };
    case 'Fair (620-659)':
      return { amount: 376, label: 'Fair' };
    case 'Poor (<600)':
      return { amount: 229, label: 'Poor' };
    default:
      return { amount: null, label: null };
  }
};

export const fieldRules = {
  first_name: {
    required: 'First name is required',
    validate: (value: string): true | string => {
      if (value.trim() === '') return 'Invalid first name';
      if (isBlockedName(value)) return 'Invalid first name';
      return true;
    },
  },
  last_name: {
    required: 'Last name is required',
    validate: (value: string): true | string => {
      if (value.trim() === '') return 'Invalid last name';
      if (isBlockedName(value)) return 'Invalid last name';
      return true;
    },
  },
  borrow_sum: {
    required: 'Field cannot be empty',
    validate: {
      min: (value: string | number): true | string => {
        const num = parseInt(value.toString().replace(/,/g, ''), 10);
        return (
          (!Number.isNaN(num) && num > 0) || 'Amount must be greater than 0'
        );
      },
      max: (value: string | number): true | string => {
        const num = parseInt(value.toString().replace(/,/g, ''), 10);
        return (
          (!Number.isNaN(num) && num <= MAX_LOAN) ||
          `Maximum loan request is $${MAX_LOAN.toLocaleString('en-US')}`
        );
      },
    },
  },
  loan_reason: {
    required: 'Select an option',
  },
  annual_income: {
    required: 'Field cannot be empty',
    validate: (value: string | number): true | string => {
      const num = parseInt(value.toString().replace(/\D/g, ''), 10);
      if (num > MAX_INCOME) return 'Please input a value less than $1,000,000';
      if (num < 1) return 'Amount must be greater than 0';
      return true;
    },
  },
  payment_frequency: {
    required: 'Select an option',
  },
  unsecured_debt_value: {
    required: 'Select an option',
  },
  address: {
    required: 'Address is required',
    validate: (value: string): true | string =>
      !/^\s*p\.?\s*o\.?\s*b(?:ox|\.?)?\b/i.test(value?.trim() || '')
        ? true
        : 'Please input your current residential address',
  },
  city: {
    required: 'City is required',
    validate: (value: string): true | string =>
      !!value?.trim() || 'City is required',
  },
  state: {
    required: 'State is required',
    validate: (value: USStates | null): true | string =>
      (!!value &&
        stateAbbreviations.includes(value?.toUpperCase() as USStates)) ||
      'Invalid state abbreviation',
  },
  zip: {
    required: 'Zip code is required',
    validate: (value: string): true | string =>
      (!!value?.trim() && value.trim().length === 5) || 'Invalid zip code',
  },
  house_ownership: {
    required: 'Select an option',
  },
  email: {
    required: 'Email is required',
    validate: {
      pattern: (value: string): true | string =>
        /^[a-zA-Z0-9._-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,3}$/.test(value) ||
        'Invalid email',
      allowedDomains: (value: string): true | string => {
        const match = value.match(/\.([a-zA-Z]{2,3})$/);
        return (
          (match && allowedEmailDomains.includes(match[1].toLowerCase())) ||
          `Only these domains are allowed: ${allowedEmailDomains.join(', ')}`
        );
      },
    },
  },
  phone: {
    required: 'Phone number is required',
    validate: validateAndFormatUSPhoneNumber,
  },
  creditRating: {
    required: 'Select an option',
  },
  educational_level: {
    required: 'Select an option',
  },
  employment_status: {
    required: 'Select an option',
  },
  date_of_birth: {
    required: 'Date of birth is required',
    validate: validateDOB,
  },
  ssn: {
    required: 'Social security number is required',
    validate: validateSSN,
  },
};
